import React from "react";
import { useTranslation } from "react-i18next";
import { PMDialog } from "../../utils/PMDialog";
import { PMField } from "../../utils/PMField";
import { Form, Formik } from "formik";
import { toast } from "react-hot-toast";
import * as Yup from "yup";
import { EmployeeSelector } from "../../utils/moduleSelectors/employeeSelector";
import PMListbox from "../../utils/PMListbox";
import { fetchWorkOrder, updateWorkOrder } from "../../../api/work-order";
import { workOrderParticipantsRole, workOrderParticipantsStatus } from "./utils";

export const WorkOrderAccessDialog = ({
  open,
  onClose,
  workOrderId,
  access,
  onSuccess,
}) => {
  const { t } = useTranslation();
  const isEditing = !!access;

  const handleSubmit = async (values, helpers) => {
    try {
      const woRes = await fetchWorkOrder(workOrderId, { fields: "*" });
      const workOrder = woRes?.data;

      const participantData = {
        user_id: values.user_id,
        role: values.role,
        status: values.status || "ASSIGNED",
      };

      let updatedParticipants = [...(workOrder.participants || [])];

      if (isEditing) {
        participantData.id = access.id;
        updatedParticipants = updatedParticipants.map((p) =>
          p.id === access.id ? { ...p, ...participantData } : p
        );
      } else {
        updatedParticipants.push(participantData);
      }

      await updateWorkOrder({
        id: workOrderId,
        participants: updatedParticipants,
      });

      const refreshed = await fetchWorkOrder(workOrderId, {
        fields: "participants",
      });
      const list = refreshed?.data?.participants || [];

      if (onSuccess) onSuccess(list);
      toast.success(
        isEditing
          ? t("work_order.participants.updated")
          : t("work_order.participants.created")
      );
      helpers.setSubmitting(false);
    } catch (err) {
      console.error("Error saving work order participant:", err);
      toast.error(t("work_order.participants.save_error"));
      helpers.setSubmitting(false);
    }
  };

  return (
    <PMDialog
      open={open}
      onClose={onClose}
      title={
        isEditing
          ? t("work_order.participants.edit")
          : t("work_order.participants.add")
      }
    >
      <div className="mt-4">
        <Formik
          initialValues={{
            user_id: access?.user?.id || null,
            role: access?.role || "EXECUTOR",
            status: access?.status || "ASSIGNED",
            submit: null,
          }}
          validationSchema={Yup.object({
            user_id: Yup.string().required(
              t("project.participants.user_required")
            ),
            role: Yup.string().required(t("project.participants.role_required")),
          })}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {(formik) => (
            <Form>
              <div className="grid grid-cols-6 gap-6">
                <div className="col-span-6 sm:col-span-6">
                  <EmployeeSelector
                    label={t("work_order.participants.user")}
                    includeHired
                    name="user_id"
                    selected={access?.user}
                    disabled={isEditing}
                    {...formik}
                  />
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <PMListbox
                    label={t("work_order.participants.role")}
                    name="role"
                    options={workOrderParticipantsRole}
                    selected={formik.values.role}
                    onChange={(e) => {
                      formik.setFieldValue("role", e.value);
                    }}
                    {...formik}
                  />
                </div>
                <div className="col-span-6 sm:col-span-3">
                  <PMListbox
                    label={t("work_order.participants.status")}
                    name="status"
                    options={workOrderParticipantsStatus}
                    selected={formik.values.status}
                    onChange={(e) => {
                      formik.setFieldValue("status", e.value);
                    }}
                    {...formik}
                  />
                </div>
              </div>

              <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                <button
                  type="button"
                  className="inline-flex justify-center w-20 rounded-md border border-transparent bg-gray-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                  onClick={onClose}
                >
                  {t("Cancel")}
                </button>

                <button
                  type="submit"
                  disabled={formik.isSubmitting}
                  className="inline-flex align-right justify-center rounded-md border border-transparent bg-emerald-600 px-4 py-2 text-base font-medium text-white shadow-sm hover:bg-blue-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm disabled:bg-gray-400"
                >
                  {formik.isSubmitting
                    ? isEditing
                      ? t("work_order.participants.updating")
                      : t("work_order.participants.adding")
                    : isEditing
                    ? t("Update")
                    : t("work_order.participants.add")}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </PMDialog>
  );
};
